<template>
  <component
    :is="dontUseOverlay === true ? 'span' : 'b-overlay'"
    :show="busy"
    rounded
    opacity="0.6"
    spinner-small
    spinner-variant="light-gray"
  >
    <b-dropdown
      v-bind="{ ...$props, ...$attrs }"
      ref="dropdownRef"
      :data-testid="dataTestId"
      variant="link"
      :class="[dropdownClasses, 'align-baseline']"
      :toggle-class="toggleClasses"
      :menu-class="menuClasses"
      no-caret
      @show="preventOpenIfDropdownNotEnabled"
      @shown="$emit('shown')"
      @hide="onHide"
      @hidden="$emit('hidden')"
    >
      <template #button-content>
        <span :style="buttonStyle">
          <span :class="fontClasses">
            <span>
              <slot name="button-content">{{ buttonText }}</slot></span
            ></span
          ><span
            v-if="hasDropdown && !hideCaret"
            :class="fontClasses"
            class="mimic-bv-caret"
          >
            <!-- IMPORTANT:
                  - leave ZERO space between this span and the preceding content
                      in order to apply appropriate spacing between the dropdown text.
                  - when using this slot, you should ensure there is NO space between
                      its content and its ending tag -->
          </span>
        </span>
      </template>
      <slot>
        <!-- default slot for dropdown content -->
      </slot>
    </b-dropdown>
  </component>
</template>

<script lang="ts">
import { ClassProp } from '@/utils/bootstrapVue';
import { BDropdown, BvEvent } from 'bootstrap-vue';
import { CSSProperties, PropType, computed, defineComponent, ref } from 'vue';

export default defineComponent({
  name: 'SentenceDropdownDeprecated',
  props: {
    /**
     * if buttonText is not supplied, then you should populate the
     * #button-content slot yourself
     */
    buttonText: {
      type: String,
      required: false,
    },
    buttonStyle: {
      type: Object as PropType<CSSProperties>,
      required: false,
      default: () => ({}),
    },
    right: {
      type: Boolean,
      required: false,
    },
    dropdownClasses: {
      type: String,
      required: false,
      default: '',
    },
    fontClasses: {
      type: String,
      required: false,
      default: 'text-info',
    },
    menuClasses: {
      type: [String, Object, Array] as PropType<ClassProp>,
      required: false,
      default: 'scroll-md shadow',
    },
    shouldTextBeVertical: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If you need to overwrite the 'boundary' prop for the dropdown in this component,
     * you may need to set the `dontUseOverlay` prop to `true` so that the 'position-relative' class that is added
     * to the automatically created wrapper for the `b-overlay` does not interfere with the
     * 'position-static' necessary in order to make the dropdown pop out of its container.
     */
    boundary: {
      type: String,
      required: false,
    },
    /**
     * If you need to overwrite the 'boundary' prop for the dropdown in this component,
     * you may need to set this prop to `true` so that the 'position-relative' class that is added
     * to the automatically created wrapper for the `b-overlay` does not interfere with the
     * 'position-static' necessary in order to make the dropdown pop out of its container.
     */
    dontUseOverlay: {
      type: Boolean,
      required: false,
      default: false,
    },
    busy: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If this is false, then do not show the caret or do anything if the button is clicked.
     * This differs from the *disabled* prop, because if that is true, then it will style the text differently
     * (i.e., disabled state)
     *
     * So this prop should be used when we want to see a plain word without a dropdown option
     */
    hasDropdown: {
      type: Boolean,
      required: false,
      default: true,
    },
    preventHide: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideCaret: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataTestId: {
      type: String,
      required: false,
    },
    includePadding: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: {
    show: () => true,
    shown: () => true,
    hide: () => true,
    hidden: () => true,
  },
  setup(props, { emit }) {
    const preventOpenIfDropdownNotEnabled = (e: BvEvent) => {
      if (!props.hasDropdown) {
        e.preventDefault();
        return;
      }
      emit('show');
    };

    const dropdownRef = ref<InstanceType<typeof BDropdown> | null>(null);

    const onHide = (bvEvent: BvEvent) => {
      if (props.preventHide) {
        bvEvent.preventDefault();
      }
      emit('hide');
    };

    const toggleClasses = computed(() => {
      return {
        'text-info text-decoration-none font-weight-500 border-transparent': true,
        'p-0': !props.includePadding,
        'cursor-text': !props.hasDropdown,
        'text-vertical-sideways': props.shouldTextBeVertical,
      };
    });

    return {
      preventOpenIfDropdownNotEnabled,
      onHide,
      toggleClasses,

      // Return so that parent can reference it
      dropdownRef,
    };
  },
});
</script>
